const isCompleted = (status) => status === 'COMPLETED';
const isUnknown = (status) => status === undefined || status === 'UNKNOWN' || status === 'No Status';
const isCancelled = (status) => !isCompleted(status) && !isUnknown(status);

const initializeStatsObject = () => ({
  total: 0,
  completed: 0,
  cancelled: 0,
  unknown: 0,
  hours: {
    scheduled: 0,
    flown: 0,
    upcoming: 0,
    lostCancelled: 0,
    lostUnderfly: 0,
    gainedOverfly: 0
  },
  byStatus: {},
  byType: {}
});

const calculateHours = (stats, flight, now) => {
  const actual = parseFloat(flight.actualHours || 0);
  const start = new Date(flight.takeoffTime);
  const end = new Date(flight.landTime);
  let scheduled = (end - start) / (1000 * 60 * 60);

  if (isNaN(scheduled)) return;
  
  // Handle midnight rollover: if landTime < takeoffTime, it's likely a next-day landing
  if (scheduled < 0) {
    scheduled += 24;
  }

  stats.hours.scheduled += scheduled;

  if (isCancelled(flight.status)) {
    stats.hours.lostCancelled += scheduled;
  } else if (isCompleted(flight.status)) {
    stats.hours.flown += actual;
    const diff = scheduled - actual;
    if (diff > 0) {
      stats.hours.lostUnderfly += diff;
    } else {
      stats.hours.gainedOverfly += Math.abs(diff);
    }
  } else if (start > now) {
    stats.hours.upcoming += scheduled;
  } else {
    // Past flight, not completed, not cancelled -> Lost Underfly
    stats.hours.lostUnderfly += scheduled;
  }
};

const updateStats = (stats, type, flight, now) => {
  const status = flight.status;
  stats.total++;
  if (isCompleted(status)) stats.completed++;
  else if (isCancelled(status)) stats.cancelled++;
  else if (isUnknown(status)) stats.unknown++;

  calculateHours(stats, flight, now);

  if (!stats.byType[type]) {
    stats.byType[type] = initializeStatsObject();
  }
  const typeStats = stats.byType[type];
  typeStats.total++;
  if (isCompleted(status)) typeStats.completed++;
  else if (isCancelled(status)) typeStats.cancelled++;
  else if (isUnknown(status)) typeStats.unknown++;
  
  calculateHours(typeStats, flight, now);

  if (status) {
    stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
    typeStats.byStatus[status] = (typeStats.byStatus[status] || 0) + 1;
  } else {
    stats.byStatus['UNKNOWN'] = (stats.byStatus['UNKNOWN'] || 0) + 1;
    typeStats.byStatus['UNKNOWN'] = (typeStats.byStatus['UNKNOWN'] || 0) + 1;
  }
};

export const fetchStats = async (firebase, startDate, endDate) => {
  const flightsSnapshot = await firebase.flights()
    .where("takeoffTime", ">=", startDate.toISOString())
    .where("takeoffTime", "<=", endDate.toISOString())
    .get();

  const stats = {
    flights: initializeStatsObject(),
    events: initializeStatsObject()
  };

  const now = new Date();

  flightsSnapshot.forEach(doc => {
    const flight = doc.data();
    updateStats(stats.flights, flight.sortieType, flight, now);

    if (flight.events) {
      Object.values(flight.events).forEach(event => {
        updateEventStats(stats.events, event.type, event.status);
      });
    }
  });

  return stats;
};

const updateEventStats = (stats, type, status) => {
  stats.total++;
  if (isCompleted(status)) stats.completed++;
  else if (isCancelled(status)) stats.cancelled++;
  else if (isUnknown(status)) stats.unknown++;

  if (status) {
    stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
  } else {
    stats.byStatus['UNKNOWN'] = (stats.byStatus['UNKNOWN'] || 0) + 1;
  }

  if (!stats.byType[type]) {
    stats.byType[type] = initializeStatsObject();
  }
  const typeStats = stats.byType[type];
  typeStats.total++;
  if (isCompleted(status)) typeStats.completed++;
  else if (isCancelled(status)) typeStats.cancelled++;
  else if (isUnknown(status)) typeStats.unknown++;
  
  if (status) {
    typeStats.byStatus[status] = (typeStats.byStatus[status] || 0) + 1;
  } else {
    typeStats.byStatus['UNKNOWN'] = (typeStats.byStatus['UNKNOWN'] || 0) + 1;
  }
};
